import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import useLegalCaseSeizedAssets, { type AddSeizedAssetValue } from '../../../../hooks/useLegalCaseSeizedAssets';
import {
    SEIZED_ASSET_STATUS_OPTIONS,
    SeizedAssetStatus,
    type SeizedAsset,
} from '../../../../types/LegalCase';
import LegalCaseSeizedAssetForm from './LegalCaseSeizedAssetForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseSeizedAssetsProps {
    caseId: string;
    initialAssets?: SeizedAsset[];
}

// Cor do selo por status do bem no ciclo da penhora. Efetivada/apreendido/leiloado em verde
// (constrição consolidada), localizado/deferida/restrição em âmbar (em curso), impenhorável
// e penhora levantada (terminais que "perderam" o bem) em cinza.
const CONSOLIDATED = new Set<SeizedAssetStatus>([
    SeizedAssetStatus.EFFECTED,
    SeizedAssetStatus.APPREHENDED,
    SeizedAssetStatus.APPRAISED,
    SeizedAssetStatus.AUCTIONED,
]);
const LOST = new Set<SeizedAssetStatus>([
    SeizedAssetStatus.UNSEIZABLE,
    SeizedAssetStatus.LIEN_LIFTED,
]);

const statusClass = (status: SeizedAssetStatus): string => {
    if (CONSOLIDATED.has(status)) {
        return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
    }
    if (LOST.has(status)) {
        return 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300';
    }
    return 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300';
};

// Resumo textual da identificação do bem, conforme o tipo. Só o que está preenchido aparece.
const identificationSummary = (asset: SeizedAsset): string => {
    const parts: string[] = [];
    if (asset.plate) parts.push(`Placa ${asset.plate}`);
    if (asset.renavam) parts.push(`RENAVAM ${asset.renavam}`);
    if (asset.chassis) parts.push(`Chassi ${asset.chassis}`);
    if (asset.state) parts.push(asset.state);
    if (asset.registrationNumber) parts.push(`Matrícula ${asset.registrationNumber}`);
    if (asset.registryOffice) parts.push(asset.registryOffice);
    if (asset.district) parts.push(asset.district);
    if (asset.serialNumber) parts.push(`Série ${asset.serialNumber}`);
    if (asset.model) parts.push(asset.model);
    if (asset.harvest) parts.push(`Safra ${asset.harvest}`);
    if (asset.volume) parts.push(asset.volume);
    if (asset.warehouse) parts.push(asset.warehouse);
    return parts.join(' • ');
};

// Uma linha de bem penhorado. Extraída para fora do componente (diretriz: sem função dentro
// de função). O dropdown de status transiciona o bem no ciclo da penhora (validado no
// backend); grau, credor concorrente e valores aparecem quando preenchidos.
const SeizedAssetItem = ({
    asset,
    onChangeStatus,
    onRemove,
    disabled,
}: {
    asset: SeizedAsset;
    onChangeStatus: (status: SeizedAssetStatus) => void;
    onRemove: () => void;
    disabled: boolean;
}) => {
    const summary = identificationSummary(asset);
    return (
        <li className="flex items-start justify-between gap-2 py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{asset.assetTypeFormatted}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${statusClass(asset.status)}`}>
                        {asset.statusFormatted}
                    </span>
                    {asset.grade != null && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{asset.grade}º grau</span>
                    )}
                </div>
                {summary && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">{summary}</div>
                )}
                {asset.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{asset.description}</p>
                )}
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap gap-x-3">
                    {asset.appraisedValueFormatted && <span>Avaliado: {asset.appraisedValueFormatted}</span>}
                    {asset.estimatedValueFormatted && !asset.appraisedValueFormatted && (
                        <span>Estimado: {asset.estimatedValueFormatted}</span>
                    )}
                    {asset.competingCreditor && <span>Credor concorrente: {asset.competingCreditor}</span>}
                    {asset.collateralAssetUuid && <span>Vinculado ao collateral</span>}
                </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <select
                    aria-label="Mudar status da penhora"
                    value={asset.status}
                    disabled={disabled}
                    onChange={(e) => onChangeStatus(e.target.value as SeizedAssetStatus)}
                    className="appearance-none text-xs rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 pl-2 pr-6 py-1 disabled:opacity-50 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22%236b7280%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22/%3E%3C/svg%3E')] bg-[length:0.85rem] bg-[right_0.35rem_center] bg-no-repeat"
                >
                    {SEIZED_ASSET_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    title="Remover bem"
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
                >
                    <FaTrash size={11} />
                </button>
            </div>
        </li>
    );
};

// Seção "Penhora" do detalhe do caso. Lista os bens penhorados, cada um com um ciclo próprio
// de status (localizado → deferida → efetivada → ... → leiloado; terminais impenhorável e
// penhora levantada). A identificação é por tipo (veículo, imóvel, maquinário, grãos). A
// remoção passa por ConfirmDialog (padrão do módulo).
const LegalCaseSeizedAssets = ({ caseId, initialAssets }: LegalCaseSeizedAssetsProps) => {
    const seized = useLegalCaseSeizedAssets(caseId, initialAssets);
    const [showForm, setShowForm] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const openForm = () => {
        seized.clearErrors();
        setShowForm(true);
    };

    const closeForm = () => {
        seized.clearErrors();
        setShowForm(false);
    };

    const submitAsset = async (value: AddSeizedAssetValue): Promise<boolean> => {
        const ok = await seized.addAsset(value);
        if (ok) closeForm();
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await seized.removeAsset(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Bens penhorados
                </h4>
                {!showForm && (
                    <button
                        type="button"
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Bem
                    </button>
                )}
            </div>

            {seized.assets.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum bem penhorado registrado</p>
            )}

            {seized.assets.length > 0 && (
                <ul>
                    {seized.assets.map((asset) => (
                        <SeizedAssetItem
                            key={asset.id}
                            asset={asset}
                            onChangeStatus={(status) => seized.changeStatus(asset.id, status)}
                            onRemove={() => setPendingRemoval(asset.id)}
                            disabled={seized.isSaving}
                        />
                    ))}
                </ul>
            )}

            {showForm && (
                <LegalCaseSeizedAssetForm
                    typeOptions={seized.typeOptions}
                    errorFields={seized.errorFields}
                    isSaving={seized.isSaving}
                    onSubmit={submitAsset}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover bem"
                message="Remover este bem penhorado do caso? Esta ação não pode ser desfeita."
                isBusy={seized.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseSeizedAssets;
